import { CoreService } from '../tools/core.service';
import { getLocalPermissionModel, storeLocalPermissionModel } from '../tools/local-storage';
import { cookiesSupported, setCookiePublisherPortal } from '../tools/cookie';
import { getTCString, setTCString } from '../tools/tcf';
import { setConsentLevel } from '../compat/consent-level';
import { updateNgUserId } from '../compat/nguserid';
import { updateUiConsentCookie } from '../compat/uiconsent';
import { updateUiCidCookie } from '../compat/uicid';
import { setIdccCookie } from '../compat/idcc';
import * as Logger from '../../common/tools/logger';
import Cookies from 'js-cookie';
import {
  CONFIG_VERSION,
  CONFIG_IDENTITY_COOKIE_IDENTIFIER
} from '../../config';
import {
  ConsentParameters,
  PermissionClient as PermissionClientInterface,
  PermissionClientHandlers,
  PermissionClientOptions,
  PermissionClientSettings
} from '../interfaces';
import { PermissionModel } from '../../interfaces';

/**
 * Provides a integration library for the United Internet Permission Core Script / CMP (Consent Management Platform) handler
 */
export class PermissionClient implements PermissionClientInterface {

  public options: PermissionClientOptions = null;
  public DEFAULT_HANDLER: PermissionClientHandlers = {
    onShowLayer: /* istanbul ignore next */ () => {},
    onResizeLayer: /* istanbul ignore next */ () => {},
    getIdentity: () => {
      return this.getIdentity();
    },
    getModel: (identity) => {
      return this.getModel(identity);
    },
    onChange: async(model: PermissionModel) => {
      await this.onChange(model);
    },
    onComplete: () => {
      this.destroy();
    },
    getTCString: () => {
      return getTCString();
    }
  };
  public version = CONFIG_VERSION;
  private moduleName = `PermissionClientApp`;
  private coreService: CoreService;

  /**
   * @public
   * @async
   * @function init
   * @description Function to initialize the Permission Client Application
   * @param {PermissionClientOptions} options Optional options
   * @return {Promise<void>}
   */
  public async init(options?: PermissionClientOptions): Promise<void> {
    try {
      if (!cookiesSupported()) {
        throw new Error('Cookies not supported');
      }

      PermissionClient.validateSettings(options as PermissionClientSettings);

      // Make the `accessToken` accessible in the `consentParameters`
      if (options.accessToken) {
        if (!options.consentParameters) {
          options.consentParameters = {};
        }
        options.consentParameters.accessToken = options.accessToken;
      }

      this.options = {
        ...this.DEFAULT_HANDLER,
        ...options
      };

      if (!this.coreService) {
        this.coreService = new CoreService();
      }
      await this.coreService.init(this.options);

      this.coreService.trackBrain({
        section: 'permission.initial.start'
      });

      Logger.debug(this.moduleName, `initialize`, `Module initialized.`);
    } catch (error) {
      if (this.options && typeof this.options.onComplete === 'function') {
        this.options.onComplete(false, error);
      } else if (options && typeof options.onComplete === 'function') {
        options.onComplete(false, error);
      } else {
        this.DEFAULT_HANDLER.onComplete(false, error);
      }
      Logger.error(this.moduleName, `init`, error);
    }
  }

  /**
   * @public
   * @async
   * @function update
   * @description Function to update the consent parameters
   * @param {ConsentParameters} consentParameters
   * @return {Promise<void>}
   */
  public async update(consentParameters?: ConsentParameters): Promise<void> {
    try {
      if (!this.coreService) {
        return;
      }

      if (consentParameters) {
        this.options.consentParameters = {
          ...(this.options.consentParameters || {}),
          ...consentParameters
        };
      }

      await this.coreService.init(this.options);
    } catch (error) {
      /* istanbul ignore next else */
      if (this.options && typeof this.options.onComplete === 'function') {
        this.options.onComplete(false, error);
      } else {
        this.DEFAULT_HANDLER.onComplete(false, error);
      }
      Logger.error(this.moduleName, `update`, error);
    }
  }

  /**
   * @private
   * @function layerDisplayed
   * @description Called by the publisher to signal that the PPP iframe is now visible
   * @return {Promise<void>}
   */
  public async layerDisplayed(): Promise<void> {
    Logger.debug(this.moduleName, 'Publisher called layerDisplayed');
    await this.coreService.layerDisplayed();
  }

  /**
   * @private
   * @function getIdentity
   * @description Default handler to get users identity from cookie
   * @return {string}
   */
  private getIdentity(): string {
    return Cookies.get(CONFIG_IDENTITY_COOKIE_IDENTIFIER) || null;
  }

  /**
   * Default handler to get the local permission model
   */
  private getModel(identity?: string): PermissionModel {
    let customerIdentity = identity;
    if (!customerIdentity && typeof this.options.getIdentity === 'function') {
      customerIdentity = this.options.getIdentity();
    }

    const model = getLocalPermissionModel(customerIdentity);
    model.identity = customerIdentity;

    if (typeof this.options.getTCString === 'function') {
      model.tcString = this.options.getTCString();
    }
    return model;
  }

  /**
   * Default handler to store the permission model locally and update the TCString within the TCF API
   */
  private async onChange(model: PermissionModel): Promise<void> {
    storeLocalPermissionModel(model);
    if (model.tcString) {
      if (!model.tcStatus) {
        Logger.warn(this.moduleName, `onChange`, `No 'tcStatus' available.`);
      }
      // Set the cookies publisher portal for cookie sync across brand domains
      setCookiePublisherPortal(this.options.portal);
      // The order of calling the functions is important
      await setIdccCookie(model.tcStatus);
      await setTCString(model.tcString);
      await setConsentLevel();
      await updateNgUserId();
      await updateUiConsentCookie();
      await updateUiCidCookie();
    }
  }

  /**
   * @private
   * @function destroy
   * @description Function to exit correctly
   * @return {void}
   */
  private destroy(): void {
    if (this.coreService && typeof this.coreService.destroy === 'function') {
      this.coreService.destroy();
    }
    this.coreService = null;
    this.options = null;
    delete this.coreService;
    delete this.options;
  }

  /**
   * @private
   * @static
   * @function validateSettings
   * @description Function to validate the publishers settings
   * @param {PermissionClientSettings} settings Optional settings
   * @return {void}
   */
  private static validateSettings(settings: PermissionClientSettings): void {
    if (!settings) {
      throw new Error('Missing settings object!');
    }

    if (!settings.portal || typeof settings.portal !== 'string') {
      throw new Error(`Required setting 'portal' must have a string value!`);
    }

    if (!settings.category || typeof settings.category !== 'string') {
      throw new Error(`Required setting 'category' must have a string value!`);
    }

    if (!!settings.section && typeof settings.section !== 'string') {
      throw new Error(`Optional setting 'section' must have a string value!`);
    }

    if (!!settings.permissionGroup && typeof settings.permissionGroup !== 'string') {
      throw new Error(`Optional setting 'permissionGroup' must have a string value!`);
    }

    if (!!settings.forcePermissionLayer && typeof settings.forcePermissionLayer !== 'string') {
      throw new Error(`Optional setting 'forcePermissionLayer' must have a string value!`);
    }

    if (!!settings.touchpointReference && settings.touchpointReference.indexOf('http') !== 0) {
      throw new Error(`Optional setting 'touchpointReference' must start with 'http://' or 'https://'!`);
    }

    if (settings.accessToken) {
      if (typeof settings.accessToken !== 'string') {
        throw new Error(`Optional setting 'accessToken' must have a string value!`);
      }
      if (settings.accessToken.indexOf('Bearer') === 0) {
        throw new Error(`Optional setting 'accessToken' contains the 'Bearer ' prefix, remove it, as it will be prepended by PPP!`);
      }
      if (!settings.xUIApp || typeof settings.xUIApp !== 'string') {
        throw new Error(`Optional setting 'accessToken' given, but related / required 'xUIApp' setting is missing or invalid!`);
      }
      if (!settings.touchpointName || typeof settings.touchpointName !== 'string') {
        throw new Error(`Optional setting 'accessToken' given, but related / required 'touchpointName' setting is missing or invalid!`);
      }
      if (!settings.touchpointReference || typeof settings.touchpointReference !== 'string') {
        throw new Error(`Optional setting 'accessToken' given, but related / required 'touchpointReference' setting is missing or invalid!`);
      }
    }
  }
}
